import {
    ActionReducerMapBuilder,
    Draft,
    SerializedError,
    createAsyncThunk,
    createSlice,
} from '@reduxjs/toolkit';
import { IAction, IAppReduxStore, SliceProps } from '../../store';
import {
    urlImportDocTypes,
    urlParamReplicaId,
    urlParamUniversalId,
    urlProjectAdditionalAddDocument,
    urlProjectAdditionalAssign,
    urlProjectDocumentDataSave,
} from '../../../Constants';
import { IFileInfoState, filesReducers, initialFileState } from '../../general/files';
import { GlobalVariables } from '../../../GlobalVariables';
import { apiFetch } from '../../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectUploadAdditionalDocumentsState = {
    isDialogOpen: false,
    isUploadCompleted: false,
    documentTypeOptions: null,
    loading: false,
    error: null,
    ...initialFileState,
};

const reducers = {
    openUploadAdditionalDialog(state: IProjectUploadAdditionalDocumentsState) {
        state.isDialogOpen = true;
    },

    closeUploadAdditionalDialog(state: IProjectUploadAdditionalDocumentsState) {
        state.isDialogOpen = false;
        state.isUploadCompleted = false;
        GlobalVariables.removeFileById(state.fileInfo?.id);
        state.fileInfo = null;
    },

    resetLoading(state: IProjectUploadAdditionalDocumentsState) {
        state.loading = false;
    },
    ...filesReducers,
};
/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    createDocument: async (config: {
        docType: string;
        replicaId: string;
        file?: File;
    }): Promise<{ docUnId: string }> => {
        try {
            const { docType, replicaId, file } = config;

            const formData = new FormData();
            formData.append('docType', docType);
            formData.append('file', file);

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };

            const url = urlProjectAdditionalAddDocument.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(response.status?.toString()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Get data for Upload Additional Dpcuments Dialog
     */
    getAdditionalDocumentTypes: async (config: { replicaId: string }) => {
        try {
            const { replicaId } = config;
            const request: RequestInit = {
                headers: {
                    docTypeKey: '#Additional#',
                },
            };

            const url = urlImportDocTypes.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    onConfirmUploadAdditionalDocuments: async (
        config: {
            replicaId: string;
            selectedDocTypeName: string;
            selectedDocTypeVariant: string;
            subject: string;
            date: number;
        },
        thunkApi,
    ): Promise<void> => {
        const { replicaId, selectedDocTypeName, selectedDocTypeVariant, subject, date } = config;

        const {
            projectUploadAdditionalDocuments: { documentTypeOptions, fileInfo },
            projectDocuments: {
                info: { data: documents },
            },
        } = thunkApi.getState() as IAppReduxStore;

        const selectedDocType = documentTypeOptions.find(
            option =>
                option.docTypeName === selectedDocTypeName &&
                option.docVariant === selectedDocTypeVariant,
        );

        const newDocument = (
            await thunkApi.dispatch(
                createDocument({
                    docType: selectedDocType.docType,
                    replicaId,
                    file: GlobalVariables.getFileById(fileInfo.id),
                }),
            )
        ).payload;

        try {
            const addDocumentData = new FormData();
            addDocumentData.append(
                'documentData',
                JSON.stringify({
                    DocVariant: selectedDocType.docVariant,
                    Subject: subject,
                    DocDate: date,
                }),
            );

            const request: RequestInit = {
                method: 'POST',
                body: addDocumentData,
            };

            const url = urlProjectDocumentDataSave
                .replace(urlParamReplicaId, replicaId)
                .replace(urlParamUniversalId, newDocument.docUnId);
            const addDocumentResponse = await apiFetch(url, request);

            if (!addDocumentResponse.ok) {
                return Promise.reject(addDocumentResponse.status);
            }
        } catch (e) {
            return Promise.reject(e as Error);
        }

        try {
            const assignDocumentsData = new FormData();
            assignDocumentsData.append('universalId', newDocument.docUnId);
            assignDocumentsData.append(
                'assignTo',
                documents.map(item => item.universalId).join('|'),
            );

            const request: RequestInit = {
                method: 'POST',
                body: assignDocumentsData,
            };

            const url = urlProjectAdditionalAssign.replace(urlParamReplicaId, replicaId);
            const assignDocumentsResponse = await apiFetch(url, request);

            return assignDocumentsResponse.ok
                ? await assignDocumentsResponse.json()
                : Promise.reject(new Error(`${assignDocumentsResponse.status}`));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectUploadAdditionalDocumentsState>) => {
        /**
         * Reducers for the 'getAdditionalDocumentTypes' thunk.
         */
        builder.addCase(
            getAdditionalDocumentTypes.pending,
            (state: Draft<IProjectUploadAdditionalDocumentsState>) => {
                state.loading = true;
                state.error = null;
            },
        );

        builder.addCase(
            getAdditionalDocumentTypes.fulfilled,
            (
                state: Draft<IProjectUploadAdditionalDocumentsState>,
                action: IAction<IDocumentTypeOption[]>,
            ) => {
                state.documentTypeOptions = action.payload.map(item => ({
                    ...item,
                    docTypeKey:
                        item.docType === '#Additional#_Testreport'
                            ? 'testReport'
                            : 'accompanyingDocument',
                }));

                state.loading = false;
                state.error = null;
            },
        );

        builder.addCase(
            getAdditionalDocumentTypes.rejected,
            (
                state: Draft<IProjectUploadAdditionalDocumentsState>,
                action: IAction<SerializedError>,
            ) => {
                state.loading = false;
                state.error = action.error;
            },
        );

        /**
         * Reducers for the 'onConfirmUploadAdditionalDocuments' thunk.
         */
        builder.addCase(
            onConfirmUploadAdditionalDocuments.pending,
            (state: Draft<IProjectUploadAdditionalDocumentsState>) => {
                state.loading = true;
                state.error = null;
            },
        );

        builder.addCase(
            onConfirmUploadAdditionalDocuments.fulfilled,
            (state: Draft<IProjectUploadAdditionalDocumentsState>) => {
                state.isUploadCompleted = true;

                state.loading = true;
                state.error = null;
                GlobalVariables.removeFileById(state.fileInfo?.id);
            },
        );

        builder.addCase(
            onConfirmUploadAdditionalDocuments.rejected,
            (
                state: Draft<IProjectUploadAdditionalDocumentsState>,
                action: IAction<SerializedError>,
            ) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

export const createDocument = createAsyncThunk(
    `${urlProjectAdditionalAddDocument}`,
    thunks.createDocument,
);

export const getAdditionalDocumentTypes = createAsyncThunk(
    `${urlImportDocTypes}`,
    thunks.getAdditionalDocumentTypes,
);

export const onConfirmUploadAdditionalDocuments = createAsyncThunk(
    `onConfirmUploadAdditionalDocuments`,
    thunks.onConfirmUploadAdditionalDocuments,
);
export interface IDocumentTypeOption {
    /** Document docType field value. */
    docType: string;

    /** Document docVariant field value. */
    docVariant: string;

    /** Document docType label value. */
    docTypeName: string;
}

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectUploadAdditionalDocumentsState extends SliceProps, IFileInfoState {
    /** Boolean value if the dialog is open. */
    isDialogOpen: boolean;

    /** Boolean value if the upload is completed. */
    isUploadCompleted: boolean;

    /** Array of document type, variant and names. */
    documentTypeOptions: IDocumentTypeOption[];
}

/**
 * Create the redux slice.
 */
export const projectUploadAdditionalDocumentsDataSlice = createSlice({
    name: 'projectUploadAdditionalDocumentsData',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const {
    closeUploadAdditionalDialog,
    openUploadAdditionalDialog,
    appendFilesInfo,
    removeFile,
    resetLoading,
} = projectUploadAdditionalDocumentsDataSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectUploadAdditionalDocumentsDataSlice.reducer;
